import { useState } from "react";
import { Code, Map, Palette, BookOpen, Zap, Lightbulb, Workflow, Brain, Sparkles, Target } from "lucide-react";

const TeamConnection = () => {
  const [activeConnection, setActiveConnection] = useState<number | null>(null);

  const roles = [
    { 
      id: 0,
      nickname: "Fallen-Son",
      name: "Code Architect", 
      icon: Code,
      gradient: "from-primary/20 to-primary/5",
      borderColor: "border-primary/50 hover:border-primary",
      textColor: "text-primary",
      superpower: "Превращает идеи в работающий код за считанные часы",
      workflow: "Проектирует архитектуру → Внедряет механики → Оптимизирует систему",
      magic: "Создает инструменты, которые ускоряют всю команду в 2 раза"
    },
    { 
      id: 1,
      nickname: "kapak",
      name: "World Builder", 
      icon: Map,
      gradient: "from-secondary/20 to-secondary/5",
      borderColor: "border-secondary/50 hover:border-secondary",
      textColor: "text-secondary",
      superpower: "Создает игровые миры, где каждый угол рассказывает историю",
      workflow: "Разрабатывает уровни → Тестирует flow → Балансирует сложность",
      magic: "Находит идеальный баланс между челленджем и удовольствием"
    },
    { 
      id: 2,
      nickname: "W-Wagner",
      name: "Visual Wizard", 
      icon: Palette,
      gradient: "from-accent/20 to-accent/5",
      borderColor: "border-accent/50 hover:border-accent",
      textColor: "text-accent",
      superpower: "Создает визуальный стиль, который запоминается навсегда",
      workflow: "Концепт-арт → Детализация → Анимация",
      magic: "Каждый пиксель имеет значение и передает эмоцию"
    },
    { 
      id: 3,
      nickname: "Marina",
      name: "Vision Keeper", 
      icon: BookOpen,
      gradient: "from-primary/15 to-accent/5",
      borderColor: "border-primary/40 hover:border-accent",
      textColor: "text-primary/90",
      superpower: "Держит фокус команды на главной цели проекта",
      workflow: "Генерирует идеи → Документирует видение → Направляет разработку",
      magic: "Превращает хаос идей в четкий план действий"
    },
  ];

  const teamDynamics = [
    {
      icon: Workflow,
      title: "Agile подход",
      description: "Ежедневные синхронизации и быстрая итерация"
    },
    {
      icon: Brain,
      title: "Коллективный разум",
      description: "Лучшие решения рождаются в дискуссиях"
    },
    {
      icon: Sparkles,
      title: "Творческая свобода",
      description: "Каждый может влиять на любой аспект игры"
    }
  ];

  return (
    <div className="mt-20 space-y-12">
      <div className="text-center space-y-4">
        <div className="inline-block">
          <div className="flex items-center gap-3 mb-3">
            <Zap className="w-8 h-8 text-accent animate-pulse" />
            <h2 className="text-4xl font-bold font-cyber terminal-text">
              <span className="text-accent">{'<'}</span>
              СИНЕРГИЯ_КОМАНДЫ
              <span className="text-accent">{'/>'}</span>
            </h2>
            <Zap className="w-8 h-8 text-accent animate-pulse" />
          </div>
        </div>
        <p className="text-sm font-mono text-muted-foreground max-w-2xl mx-auto">
          <span className="text-secondary">{'// '}</span>
          Как мы работаем вместе, чтобы создавать игры
        </p>
      </div>

      {/* Team Dynamics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {teamDynamics.map((dynamic, i) => {
          const DynamicIcon = dynamic.icon;
          return (
            <div 
              key={i}
              className="cyber-card p-6 bg-gradient-to-br from-background via-primary/5 to-background hover:scale-105 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-sm border border-accent/50 bg-accent/10">
                  <DynamicIcon className="w-8 h-8 text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-cyber font-bold text-accent mb-2">{dynamic.title}</h3>
                  <p className="text-sm font-mono text-foreground/70">{dynamic.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="relative">
        {/* Connection Lines Background */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
          <defs>
            <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
              <stop offset="50%" stopColor="hsl(var(--accent))" stopOpacity="0.2" />
              <stop offset="100%" stopColor="hsl(var(--secondary))" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          {activeConnection !== null && (
            <>
              <line x1="25%" y1="25%" x2="75%" y2="25%" stroke="url(#connectionGradient)" strokeWidth="2" className="animate-pulse" />
              <line x1="25%" y1="75%" x2="75%" y2="75%" stroke="url(#connectionGradient)" strokeWidth="2" className="animate-pulse" />
              <line x1="25%" y1="25%" x2="25%" y2="75%" stroke="url(#connectionGradient)" strokeWidth="2" className="animate-pulse" />
              <line x1="75%" y1="25%" x2="75%" y2="75%" stroke="url(#connectionGradient)" strokeWidth="2" className="animate-pulse" />
              <line x1="25%" y1="25%" x2="75%" y2="75%" stroke="url(#connectionGradient)" strokeWidth="2" className="animate-pulse" />
              <line x1="75%" y1="25%" x2="25%" y2="75%" stroke="url(#connectionGradient)" strokeWidth="2" className="animate-pulse" />
            </>
          )}
        </svg>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative" style={{ zIndex: 1 }}>
          {roles.map((role) => {
            const Icon = role.icon;
            const isActive = activeConnection === role.id;
            
            return (
              <div
                key={role.id}
                onMouseEnter={() => setActiveConnection(role.id)}
                onMouseLeave={() => setActiveConnection(null)}
                className={`cyber-card p-8 cursor-pointer transition-all duration-500 group ${
                  isActive 
                    ? `${role.borderColor} scale-105 shadow-2xl bg-gradient-to-br ${role.gradient}` 
                    : "border-muted/30 hover:border-muted/60"
                }`}
              >
                  <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-start gap-6">
                    <div className={`p-4 rounded-sm border ${role.borderColor} bg-background/50 transition-all duration-300 ${
                      isActive ? "scale-110 rotate-6" : "group-hover:scale-105"
                    }`}>
                      <Icon className={`w-10 h-10 ${role.textColor} ${isActive ? "animate-pulse" : ""}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-2xl font-cyber font-bold mb-1 ${isActive ? role.textColor : "text-foreground"}`}>
                        {role.name}
                      </h3>
                      <p className={`font-mono text-base mb-1 ${isActive ? "text-accent" : "text-muted-foreground"}`}>
                        <span className="text-secondary">@</span>{role.nickname}
                      </p>
                      <p className={`font-mono text-sm ${isActive ? "text-accent" : "text-muted-foreground"}`}>
                        {'>> '}{isActive ? 'ACTIVATED' : 'HOVER TO ACTIVATE'}
                      </p>
                    </div>
                  </div>

                  {/* Content - только при активации */}
                  <div className={`transition-all duration-500 space-y-4 ${
                    isActive ? "opacity-100 max-h-96" : "opacity-0 max-h-0 overflow-hidden"
                  }`}>
                    {/* Superpower */}
                    <div className={`cyber-card p-4 bg-gradient-to-r ${role.gradient} border ${role.borderColor}`}>
                      <div className="flex items-start gap-2">
                        <Lightbulb className={`w-5 h-5 ${role.textColor} flex-shrink-0 mt-0.5`} />
                        <div>
                          <p className="text-xs font-mono font-bold mb-1 text-accent">СУПЕРСИЛА:</p>
                          <p className="font-mono text-sm text-foreground/90">
                            {role.superpower}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Workflow */}
                    <div className={`cyber-card p-4 bg-background/80 border ${role.borderColor}`}>
                      <div className="flex items-start gap-2">
                        <Target className={`w-5 h-5 ${role.textColor} flex-shrink-0 mt-0.5`} />
                        <div>
                          <p className="text-xs font-mono font-bold mb-1 text-secondary">РАБОЧИЙ ПРОЦЕСС:</p>
                          <p className="font-mono text-sm text-foreground/80">
                            {role.workflow}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Magic */}
                    <div className={`cyber-card p-4 bg-gradient-to-r ${role.gradient} border ${role.borderColor}`}>
                      <div className="flex items-start gap-2">
                        <Sparkles className={`w-5 h-5 ${role.textColor} flex-shrink-0 mt-0.5 animate-pulse`} />
                        <div>
                          <p className="text-xs font-mono font-bold mb-1 text-primary">МАГИЯ:</p>
                          <p className="font-mono text-sm text-foreground/90">
                            {role.magic}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="cyber-card p-8 bg-gradient-to-r from-primary/5 via-accent/5 to-secondary/5 text-center border-primary/30">
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
            <Zap className="w-6 h-6 text-primary animate-pulse" />
            <div className="h-px flex-1 bg-gradient-to-r from-primary via-transparent to-transparent"></div>
          </div>
          <p className="font-mono text-base text-foreground/80 max-w-3xl mx-auto leading-relaxed">
            <span className="text-primary font-bold">{'> '}</span>
            Мы не просто работаем вместе — мы создаем экосистему, где каждый усиливает других. 
            Наша сила в том, что мы слушаем друг друга, спорим, экспериментируем и не боимся ошибаться.
          </p>
          <p className="font-terminal text-accent text-sm animate-pulse pt-2">
            {'>> '} РЕЗУЛЬТАТ = СУММА_ТАЛАНТОВ × СИНЕРГИЯ {'_'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeamConnection;
